import { Injectable } from '@angular/core';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { ServerResponse } from '../../interfaces/base/server.response';
import { ApiEndpointsService } from './api-endpoints.service';
import { AddressBookType } from '../../interfaces/configuration/address-book-type';
import { environment } from '../../../environments/environment';
import { Department } from '../../interfaces/routering/department';
import { User } from '../../interfaces/routering/user';
import { Role } from '../../interfaces/routering/role';
import { HistoryType } from '../../interfaces/configuration/history-type';
import { Source } from '../../interfaces/routering/source';
import { Status } from '../../interfaces/configuration/status';
import { BasicRegistration } from '../../interfaces/routering/basic-registration';
import { TaskRegistration } from '../../interfaces/routering/task-registration';
import { AddressBook } from '../../interfaces/configuration/address-book';
import { StandardReaction } from '../../interfaces/configuration/standard-reaction';
import { IntegratedOptions } from '../../interfaces/base/integrated-options';
import { ChangelogType } from '../../interfaces/configuration/changelog-type';
import { Permission } from '../../interfaces/routering/permission';
import { MailEvent } from '../../interfaces/sent-emails/mail-event';
import { MailLogLevel } from '../../interfaces/sent-emails/mail-log-level';
import { FilterData, Filters } from '../../interfaces/base/filters';
import { Priority } from '../../interfaces/configuration/priority';
import { DomainType } from '../../interfaces/configuration/domain-type';
import { Month } from '../../interfaces/base/month';
import { BroadcastService } from './broadcast.service';
import { HistoryEntryType } from '../../interfaces/configuration/history-entry-type';
import { Action } from '../../interfaces/routering/action';
import { Tile } from '../../interfaces/routering/tile';
import { Current } from '../../interfaces/configuration/current';
import { ImpactRegistration } from '../../interfaces/configuration/impact-registration';

@Injectable({
  providedIn: 'root'
})

export class FiltersService {

  public filters: Filters = null;

  private filterData: FilterData = {
    actions: [],
    addressBook: [],
    addressBookTypes: [],
    basicRegistrations: [],
    changelogTypes: [],
    currents: [],
    departments: [],
    departmentsAndUsers: [],
    domainTypes: [],
    historyEntryTypes: [],
    historyTypes: [],
    impactRegistrations: [],
    mailEvents: [],
    mailLogLevels: [],
    mailSubjects: [],
    permissionGroups: [],
    permissions: [],
    priorities: [],
    roles: [],
    sources: [],
    statuses: [],
    standardReactions: [],
    taskRegistrations: [],
    tiles: [],
    users: [],
    usersCanBeAssigned: [],
  };

  constructor(private api: ApiService,
              private apiEndpoints: ApiEndpointsService,
              private broadcastService: BroadcastService,
  ) {
  }

  yesNo(): Observable<IntegratedOptions[]> {
    return new Observable<IntegratedOptions[]>((o: Subscriber<IntegratedOptions[]>): void => {
      o.next([
        {
          name: 'Ja',
          id: true
        },
        {
          name: 'Nee',
          id: false
        }
      ]);
    });
  }

  for(): Observable<IntegratedOptions[]> {
    return new Observable<IntegratedOptions[]>((o: Subscriber<IntegratedOptions[]>): void => {
      o.next([
        {
          name: 'Afwijzen',
          id: 'rejected'
        },
        {
          name: 'Voltooien',
          id: 'resolved'
        },
      ]);
    });
  }

  months(): Observable<Month[]> {
    return new Observable<Month[]>((o: Subscriber<Month[]>): void => {
      o.next([
        {
          name: 'Januari',
          short_name: 'Jan',
          id: 1
        },
        {
          name: 'Februari',
          short_name: 'Feb',
          id: 2
        },
        {
          name: 'Maart',
          short_name: 'Maa',
          id: 3
        },
        {
          name: 'April',
          short_name: 'Apr',
          id: 4
        },
        {
          name: 'Mei',
          short_name: 'Mei',
          id: 5
        },
        {
          name: 'Juni',
          short_name: 'Jun',
          id: 6
        },
        {
          name: 'Juli',
          short_name: 'Jul',
          id: 7
        },
        {
          name: 'Augustus',
          short_name: 'Aug',
          id: 8
        },
        {
          name: 'September',
          short_name: 'Sep',
          id: 9
        },
        {
          name: 'Oktober',
          short_name: 'Okt',
          id: 10
        },
        {
          name: 'November',
          short_name: 'Nov',
          id: 11
        },
        {
          name: 'December',
          short_name: 'Dec',
          id: 12
        },
      ]);
    });
  }

  departments(): Observable<Department[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Department[]>((o: Subscriber<Department[]>) => {
      const emitData = (data: Department[]): void => {
        o.next(data);
        this.broadcastService.clearStale('departments.changed');

        this.filterData.departments = data;
        if (this.filters?.departments) {
          this.filters.departments.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Department[]> => {
        if (this.filters?.departments) {
          this.filters.departments.isLoading = true;
        }
        return this.getDepartments();
      };

      if (this.filterData.departments.length && !this.broadcastService.isStale('departments.changed')) {
        o.next(this.filterData.departments);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('departments', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: departments.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getDepartments(): Observable<Department[]> {
    return new Observable<Department[]>((o: Subscriber<Department[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.departments'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  departmentUsers(department_id: number): Observable<User[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsSub2: Subscription = null,
      wsDataSub: Subscription = null,
      wsDataSub2: Subscription = null;
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const emitData = (data: User[]): void => {
        o.next(data);
        if (this.filters?.departmentUsers) {
          this.filters.departmentUsers.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<User[]> => {
        if (this.filters?.departmentUsers) {
          this.filters.departmentUsers.isLoading = true;
        }
        return this.getDepartmentUsers(department_id);
      };

      initialSub = loadFromServer().subscribe(emitData);

      wsSub = this.broadcastService.getEventSubject('users', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: users.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      wsSub2 = this.broadcastService.getEventSubject('departments', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: departments.changed');
        }

        wsDataSub2?.unsubscribe();

        wsDataSub2 = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsSub2?.unsubscribe();
        wsDataSub?.unsubscribe();
        wsDataSub2?.unsubscribe();
      };
    });
  }

  getDepartmentUsers(department_id: number): Observable<User[]> {
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.department-users', {':id': department_id}), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  permissions(): Observable<Permission[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Permission[]>((o: Subscriber<Permission[]>) => {
      const emitData = (data: Permission[]): void => {
        o.next(data);
        this.filterData.permissions = data;
        this.broadcastService.clearStale('permissions.changed');

        if (this.filters?.permissions) {
          this.filters.permissions.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Permission[]> => {
        if (this.filters?.permissions) {
          this.filters.permissions.isLoading = true;
        }
        return this.getPermissions();
      };

      if (this.filterData.permissions.length && !this.broadcastService.isStale('permissions.changed')) {
        o.next(this.filterData.permissions);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('permissions', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: permissions.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getPermissions(): Observable<Permission[]> {
    return new Observable<Permission[]>((o: Subscriber<Permission[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.permissions'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  permissionGroups(): Observable<Permission[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Permission[]>((o: Subscriber<Permission[]>) => {
      const emitData = (data: Permission[]): void => {
        o.next(data);
        this.filterData.permissionGroups = data;
        this.broadcastService.clearStale('permissionGroups.changed');

        if (this.filters?.permissionGroups) {
          this.filters.permissionGroups.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Permission[]> => {
        if (this.filters?.permissionGroups) {
          this.filters.permissionGroups.isLoading = true;
        }
        return this.getPermissionGroups();
      };

      if (this.filterData.permissionGroups.length && !this.broadcastService.isStale('permissionGroups.changed')) {
        o.next(this.filterData.permissionGroups);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('permissionGroups', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: permissionGroups.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getPermissionGroups(): Observable<Permission[]> {
    return new Observable<Permission[]>((o: Subscriber<Permission[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.permission-groups'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  roles(): Observable<Role[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Role[]>((o: Subscriber<Role[]>) => {
      const emitData = (data: Role[]): void => {
        o.next(data);
        this.filterData.roles = data;
        this.broadcastService.clearStale('roles.changed');

        if (this.filters?.roles) {
          this.filters.roles.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Role[]> => {
        if (this.filters?.roles) {
          this.filters.roles.isLoading = true;
        }
        return this.getRoles();
      };

      if (this.filterData.roles.length && !this.broadcastService.isStale('roles.changed')) {
        o.next(this.filterData.roles);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('roles', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: roles.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getRoles(): Observable<Role[]> {
    return new Observable<Role[]>((o: Subscriber<Role[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.roles'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  users(): Observable<User[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const emitData = (data: User[]): void => {
        o.next(data);
        this.filterData.users = data;
        this.broadcastService.clearStale('users.changed');

        if (this.filters?.users) {
          this.filters.users.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<User[]> => {
        if (this.filters?.users) {
          this.filters.users.isLoading = true;
        }
        return this.getUsers();
      };

      if (this.filterData.users.length && !this.broadcastService.isStale('users.changed')) {
        o.next(this.filterData.users);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('users', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: users.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getUsers(): Observable<User[]> {
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.users'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  usersCanBeAssigned(): Observable<User[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const emitData = (data: User[]): void => {
        o.next(data);
        this.filterData.usersCanBeAssigned = data;
        this.broadcastService.clearStale('users.changed');

        if (this.filters?.usersCanBeAssigned) {
          this.filters.usersCanBeAssigned.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<User[]> => {
        if (this.filters?.usersCanBeAssigned) {
          this.filters.usersCanBeAssigned.isLoading = true;
        }
        return this.getUsersCanBeAssigned();
      };

      if (this.filterData.usersCanBeAssigned.length && !this.broadcastService.isStale('users.changed')) {
        o.next(this.filterData.usersCanBeAssigned);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('users', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: users.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getUsersCanBeAssigned(): Observable<User[]> {
    return new Observable<User[]>((o: Subscriber<User[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.users.can-be-assigned'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }


  historyEntryTypes(): Observable<HistoryEntryType[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<HistoryType[]>((o: Subscriber<HistoryEntryType[]>) => {
      const emitData = (data: HistoryEntryType[]): void => {
        o.next(data);
        this.filterData.historyEntryTypes = data;
        this.broadcastService.clearStale('historyEntryTypes.changed');

        if (this.filters?.historyEntryTypes) {
          this.filters.historyEntryTypes.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<HistoryEntryType[]> => {
        if (this.filters?.historyEntryTypes) {
          this.filters.historyEntryTypes.isLoading = true;
        }
        return this.getHistoryEntryTypes();
      };

      if (this.filterData.historyEntryTypes.length && !this.broadcastService.isStale('historyEntryTypes.changed')) {
        o.next(this.filterData.historyEntryTypes);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('historyEntryTypes', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: historyEntryTypes.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getHistoryEntryTypes(): Observable<HistoryEntryType[]> {
    return new Observable<HistoryEntryType[]>((o: Subscriber<HistoryEntryType[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.history-entry-types'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  historyTypes(): Observable<HistoryType[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<HistoryType[]>((o: Subscriber<HistoryType[]>) => {
      const emitData = (data: HistoryType[]): void => {
        o.next(data);
        this.filterData.historyTypes = data;
        this.broadcastService.clearStale('historyTypes.changed');

        if (this.filters?.historyTypes) {
          this.filters.historyTypes.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<HistoryType[]> => {
        if (this.filters?.historyTypes) {
          this.filters.historyTypes.isLoading = true;
        }
        return this.getHistoryTypes();
      };

      if (this.filterData.historyTypes.length && !this.broadcastService.isStale('historyTypes.changed')) {
        o.next(this.filterData.historyTypes);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('historyTypes', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: historyTypes.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getHistoryTypes(): Observable<HistoryType[]> {
    return new Observable<HistoryType[]>((o: Subscriber<HistoryType[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.history-types'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  priorities(): Observable<Priority[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Priority[]>((o: Subscriber<Priority[]>) => {
      const emitData = (data: Priority[]): void => {
        o.next(data);
        this.filterData.priorities = data;
        this.broadcastService.clearStale('priorities.changed');

        if (this.filters?.priorities) {
          this.filters.priorities.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Priority[]> => {
        if (this.filters?.priorities) {
          this.filters.priorities.isLoading = true;
        }
        return this.getPriorities();
      };

      if (this.filterData.priorities.length && !this.broadcastService.isStale('priorities.changed')) {
        o.next(this.filterData.priorities);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('priorities', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: priorities.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getPriorities(): Observable<Priority[]> {
    return new Observable<Priority[]>((o: Subscriber<Priority[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.priorities'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  domainTypes(): Observable<DomainType[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<DomainType[]>((o: Subscriber<DomainType[]>) => {
      const emitData = (data: DomainType[]): void => {
        o.next(data);
        this.filterData.domainTypes = data;
        this.broadcastService.clearStale('domainTypes.changed');

        if (this.filters?.domainTypes) {
          this.filters.domainTypes.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<DomainType[]> => {
        if (this.filters?.domainTypes) {
          this.filters.domainTypes.isLoading = true;
        }
        return this.getDomainTypes();
      };

      if (this.filterData.domainTypes.length && !this.broadcastService.isStale('domainTypes.changed')) {
        o.next(this.filterData.domainTypes);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('domainTypes', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: domainTypes.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getDomainTypes(): Observable<DomainType[]> {
    return new Observable<DomainType[]>((o: Subscriber<DomainType[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.domain-types'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  sources(): Observable<Source[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Source[]>((o: Subscriber<Source[]>) => {
      const emitData = (data: Source[]): void => {
        o.next(data);
        this.filterData.sources = data;
        this.broadcastService.clearStale('sources.changed');

        if (this.filters?.sources) {
          this.filters.sources.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Source[]> => {
        if (this.filters?.sources) {
          this.filters.sources.isLoading = true;
        }
        return this.getSources();
      };

      if (this.filterData.sources.length && !this.broadcastService.isStale('sources.changed')) {
        o.next(this.filterData.sources);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('sources', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: sources.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getSources(): Observable<Source[]> {
    return new Observable<Source[]>((o: Subscriber<Source[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.sources'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  actions(): Observable<Action[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Action[]>((o: Subscriber<Action[]>) => {
      const emitData = (data: Action[]): void => {
        o.next(data);
        this.filterData.actions = data;
        this.broadcastService.clearStale('actions.changed');

        if (this.filters?.actions) {
          this.filters.actions.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Action[]> => {
        if (this.filters?.actions) {
          this.filters.actions.isLoading = true;
        }
        return this.getActions();
      };

      if (this.filterData.actions.length && !this.broadcastService.isStale('actions.changed')) {
        o.next(this.filterData.actions);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('actions', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: actions.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getActions(): Observable<Action[]> {
    return new Observable<Action[]>((o: Subscriber<Action[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.actions'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  tiles(): Observable<Tile[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Tile[]>((o: Subscriber<Tile[]>) => {
      const emitData = (data: Tile[]): void => {
        o.next(data);
        this.filterData.tiles = data;
        this.broadcastService.clearStale('tiles.changed');

        if (this.filters?.tiles) {
          this.filters.tiles.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Tile[]> => {
        if (this.filters?.tiles) {
          this.filters.tiles.isLoading = true;
        }
        return this.getTiles();
      };

      if (this.filterData.tiles.length && !this.broadcastService.isStale('tiles.changed')) {
        o.next(this.filterData.tiles);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('tiles', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: tiles.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getTiles(): Observable<Tile[]> {
    return new Observable<Tile[]>((o: Subscriber<Tile[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.tiles'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  statuses(): Observable<Status[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Status[]>((o: Subscriber<Status[]>) => {
      const emitData = (data: Status[]): void => {
        o.next(data);
        this.filterData.statuses = data;
        this.broadcastService.clearStale('statuses.changed');

        if (this.filters?.statuses) {
          this.filters.statuses.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Status[]> => {
        if (this.filters?.statuses) {
          this.filters.statuses.isLoading = true;
        }
        return this.getStatuses();
      };

      if (this.filterData.statuses.length && !this.broadcastService.isStale('statuses.changed')) {
        o.next(this.filterData.statuses);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('statuses', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: statuses.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getStatuses(): Observable<Status[]> {
    return new Observable<Status[]>((o: Subscriber<Status[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.statuses'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  currents(): Observable<Current[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<Current[]>((o: Subscriber<Current[]>) => {
      const emitData = (data: Current[]): void => {
        o.next(data);
        this.filterData.currents = data;
        this.broadcastService.clearStale('currents.changed');

        if (this.filters?.currents) {
          this.filters.currents.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<Current[]> => {
        if (this.filters?.currents) {
          this.filters.currents.isLoading = true;
        }
        return this.getCurrents();
      };

      if (this.filterData.currents.length && !this.broadcastService.isStale('currents.changed')) {
        o.next(this.filterData.currents);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('currents', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: currents.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getCurrents(): Observable<Current[]> {
    return new Observable<Current[]>((o: Subscriber<Current[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.currents'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  impactRegistrations(): Observable<ImpactRegistration[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<ImpactRegistration[]>((o: Subscriber<ImpactRegistration[]>) => {
      const emitData = (data: ImpactRegistration[]): void => {
        o.next(data);
        this.filterData.impactRegistrations = data;
        this.broadcastService.clearStale('impactRegistrations.changed');

        if (this.filters?.impactRegistrations) {
          this.filters.impactRegistrations.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<ImpactRegistration[]> => {
        if (this.filters?.impactRegistrations) {
          this.filters.impactRegistrations.isLoading = true;
        }
        return this.getImpactRegistrations();
      };

      if (this.filterData.impactRegistrations.length && !this.broadcastService.isStale('impactRegistrations.changed')) {
        o.next(this.filterData.impactRegistrations);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('impactRegistrations', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: impactRegistrations.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getImpactRegistrations(): Observable<ImpactRegistration[]> {
    return new Observable<ImpactRegistration[]>((o: Subscriber<ImpactRegistration[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.impact-registrations'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }


  basicRegistrations(): Observable<BasicRegistration[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<BasicRegistration[]>((o: Subscriber<BasicRegistration[]>) => {
      const emitData = (data: BasicRegistration[]): void => {
        o.next(data);
        this.filterData.basicRegistrations = data;
        this.broadcastService.clearStale('basicRegistrations.changed');

        if (this.filters?.basicRegistrations) {
          this.filters.basicRegistrations.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<BasicRegistration[]> => {
        if (this.filters?.basicRegistrations) {
          this.filters.basicRegistrations.isLoading = true;
        }
        return this.getBasicRegistrations();
      };

      if (this.filterData.basicRegistrations.length && !this.broadcastService.isStale('basicRegistrations.changed')) {
        o.next(this.filterData.basicRegistrations);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('basicRegistrations', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: basicRegistrations.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });

  }

  getBasicRegistrations(): Observable<BasicRegistration[]> {
    return new Observable<BasicRegistration[]>((o: Subscriber<BasicRegistration[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.basic-registrations')).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  taskRegistrations(basic_registration_id: number = null): Observable<TaskRegistration[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<TaskRegistration[]>((o: Subscriber<TaskRegistration[]>) => {
      const emitData = (data: TaskRegistration[]): void => {
        o.next(data);
        if (basic_registration_id !== null) {
          this.filterData.taskRegistrations[basic_registration_id] = data;
        } else {
          this.filterData.taskRegistrations['null'] = data;
        }
        this.broadcastService.clearStale('taskRegistrations.changed');

        if (this.filters?.taskRegistrations) {
          this.filters.taskRegistrations.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<TaskRegistration[]> => {
        if (this.filters?.taskRegistrations) {
          this.filters.taskRegistrations.isLoading = true;
        }
        return this.getTaskRegistrations(basic_registration_id);
      };

      if (
        (
          basic_registration_id === null && typeof this.filterData.taskRegistrations['null'] !== 'undefined' && this.filterData.taskRegistrations['null'].length ||
          basic_registration_id !== null && typeof this.filterData.taskRegistrations[basic_registration_id] !== 'undefined' && this.filterData.taskRegistrations[basic_registration_id].length
        ) && !this.broadcastService.isStale('taskRegistrations.changed')
      ) {
        if (basic_registration_id !== null) {
          o.next(this.filterData.taskRegistrations[basic_registration_id]);
        } else {
          o.next(this.filterData.taskRegistrations['null']);
        }
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('taskRegistrations', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: taskRegistrations.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });

  }

  getTaskRegistrations(basic_registration_id: number): Observable<TaskRegistration[]> {
    return new Observable<TaskRegistration[]>((o: Subscriber<TaskRegistration[]>) => {
      const subscription: Subscription = this.api.get((
        basic_registration_id
          ? this.apiEndpoints.get('filters.basic-registrations.task-registrations', {':basic_registration_id': basic_registration_id})
          : this.apiEndpoints.get('filters.task-registrations')
      ), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }


  addressBook(): Observable<AddressBook[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<AddressBook[]>((o: Subscriber<AddressBook[]>) => {
      const emitData = (data: AddressBook[]): void => {
        o.next(data);
        this.filterData.addressBook = data;
        this.broadcastService.clearStale('addressBooks.changed');

        if (this.filters?.addressBook) {
          this.filters.addressBook.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<AddressBook[]> => {
        if (this.filters?.addressBook) {
          this.filters.addressBook.isLoading = true;
        }
        return this.getAddressBooks();
      };

      if (this.filterData.addressBook.length && !this.broadcastService.isStale('addressBooks.changed')) {
        o.next(this.filterData.addressBook);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('addressBooks', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: addressBooks.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getAddressBooks(): Observable<AddressBook[]> {
    return new Observable<AddressBook[]>((o: Subscriber<AddressBook[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.address-book'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  addressBookTypes(): Observable<AddressBookType[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<AddressBookType[]>((o: Subscriber<AddressBookType[]>) => {
      const emitData = (data: AddressBookType[]): void => {
        o.next(data);
        this.filterData.addressBookTypes = data;
        this.broadcastService.clearStale('addressBookTypes.changed');

        if (this.filters?.addressBookTypes) {
          this.filters.addressBookTypes.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<AddressBookType[]> => {
        if (this.filters?.addressBookTypes) {
          this.filters.addressBookTypes.isLoading = true;
        }
        return this.getAddressBookTypes();
      };

      if (this.filterData.addressBookTypes.length && !this.broadcastService.isStale('addressBookTypes.changed')) {
        o.next(this.filterData.addressBookTypes);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('addressBookTypes', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: addressBookTypes.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getAddressBookTypes(): Observable<AddressBookType[]> {
    return new Observable<AddressBookType[]>((o: Subscriber<AddressBookType[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.address-book-types'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  standardReactions(): Observable<StandardReaction[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<StandardReaction[]>((o: Subscriber<StandardReaction[]>) => {
      const emitData = (data: StandardReaction[]): void => {
        o.next(data);
        this.filterData.standardReactions = data;
        this.broadcastService.clearStale('standardReactions.changed');

        if (this.filters?.standardReactions) {
          this.filters.standardReactions.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<StandardReaction[]> => {
        if (this.filters?.standardReactions) {
          this.filters.standardReactions.isLoading = true;
        }
        return this.getStandardReactions();
      };

      if (this.filterData.standardReactions.length && !this.broadcastService.isStale('standardReactions.changed')) {
        o.next(this.filterData.standardReactions);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('standardReactions', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: standardReactions.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getStandardReactions(): Observable<StandardReaction[]> {
    return new Observable<StandardReaction[]>((o: Subscriber<StandardReaction[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.standard-reactions'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }


  changelogTypes(): Observable<ChangelogType[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<ChangelogType[]>((o: Subscriber<ChangelogType[]>) => {
      const emitData = (data: ChangelogType[]): void => {
        o.next(data);
        this.filterData.changelogTypes = data;
        this.broadcastService.clearStale('changelogTypes.changed');

        if (this.filters?.changelogTypes) {
          this.filters.changelogTypes.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<ChangelogType[]> => {
        if (this.filters?.changelogTypes) {
          this.filters.changelogTypes.isLoading = true;
        }
        return this.getChangelogTypes();
      };

      if (this.filterData.changelogTypes.length && !this.broadcastService.isStale('changelogTypes.changed')) {
        o.next(this.filterData.changelogTypes);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('changelogTypes', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: changelogTypes.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getChangelogTypes(): Observable<ChangelogType[]> {
    return new Observable<ChangelogType[]>((o: Subscriber<ChangelogType[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.knowledge-base.changelogs.types'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }


  mailEvents(): Observable<MailEvent[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<MailEvent[]>((o: Subscriber<MailEvent[]>) => {
      const emitData = (data: MailEvent[]): void => {
        o.next(data);
        this.filterData.mailEvents = data;
        this.broadcastService.clearStale('mailEvents.changed');

        if (this.filters?.mailEvents) {
          this.filters.mailEvents.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<MailEvent[]> => {
        if (this.filters?.mailEvents) {
          this.filters.mailEvents.isLoading = true;
        }
        return this.getMailEvents();
      };

      if (this.filterData.mailEvents.length && !this.broadcastService.isStale('mailEvents.changed')) {
        o.next(this.filterData.mailEvents);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('mailEvents', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: mailEvents.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getMailEvents(): Observable<MailEvent[]> {
    return new Observable<MailEvent[]>((o: Subscriber<MailEvent[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.sent-emails.events'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }

  mailLogLevels(): Observable<MailLogLevel[]> {
    let initialSub: Subscription = null,
      wsSub: Subscription = null,
      wsDataSub: Subscription = null;
    return new Observable<MailLogLevel[]>((o: Subscriber<MailLogLevel[]>) => {
      const emitData = (data: MailLogLevel[]): void => {
        o.next(data);
        this.filterData.mailLogLevels = data;
        this.broadcastService.clearStale('mailLogLevels.changed');

        if (this.filters?.mailLogLevels) {
          this.filters.mailLogLevels.isLoading = false;
        }
      };

      const loadFromServer = (): Observable<MailLogLevel[]> => {
        if (this.filters?.mailLogLevels) {
          this.filters.mailLogLevels.isLoading = true;
        }
        return this.getMailLogLevels();
      };

      if (this.filterData.mailLogLevels.length && !this.broadcastService.isStale('mailLogLevels.changed')) {
        o.next(this.filterData.mailLogLevels);
      } else {
        initialSub = loadFromServer().subscribe(emitData);
      }

      wsSub = this.broadcastService.getEventSubject('mailLogLevels', 'changed').subscribe(() => {
        if (!environment.production) {
          console.log('WebSocket: received event for "filters" channel: mailLogLevels.changed');
        }

        wsDataSub?.unsubscribe();

        wsDataSub = loadFromServer().subscribe(emitData);
      });

      return (): void => {
        initialSub?.unsubscribe();
        wsSub?.unsubscribe();
        wsDataSub?.unsubscribe();
      };
    });
  }

  getMailLogLevels(): Observable<MailLogLevel[]> {
    return new Observable<MailLogLevel[]>((o: Subscriber<MailLogLevel[]>) => {
      const subscription: Subscription = this.api.get(this.apiEndpoints.get('filters.sent-emails.log-levels'), null, true).subscribe(
        (response: ServerResponse): void => {
          if (typeof response.data !== 'undefined') {
            o.next(response.data);
            o.complete();
          } else {
            o.next([]);
            o.complete();
          }
        },
        (): void => {
          o.next([]);
          o.complete();
        });

      return (): void => {
        subscription.unsubscribe();
      };
    });
  }
}
